// Drives the "build your own bundle" block rendered by snippets/bundle-builder.liquid.
//
// Each product in the bundle is posted as its own cart line (repeats collapsed into one line with
// a quantity). The add itself follows assets/product-bundle.js: same /cart/add call, same cart
// drawer refresh, same cartUpdate publish and CartLinesUpdateEvent, so anything already listening
// to the theme's cart traffic sees this add exactly like any other.
//
// A bundle in progress is kept in localStorage and mirrored in memory. Only tiers the shopper has
// actually edited get written, so an untouched tier keeps following the merchant's defaults.
const BUNDLE_BUILDER_STORAGE_PREFIX = 'nootie:bundle-builder:';
const bundleBuilderStates = new Map();

function readBundleBuilderState(key) {
  if (bundleBuilderStates.has(key)) return bundleBuilderStates.get(key);

  let stored = null;
  try {
    stored = JSON.parse(localStorage.getItem(BUNDLE_BUILDER_STORAGE_PREFIX + key) || 'null');
  } catch (e) {
    // Unavailable in private mode or with site data blocked, fall through to a blank state.
  }

  const state = {
    activeQuantity: stored && stored.activeQuantity ? stored.activeQuantity : null,
    tiers: stored && stored.tiers && typeof stored.tiers === 'object' ? stored.tiers : {},
  };

  bundleBuilderStates.set(key, state);
  return state;
}

function writeBundleBuilderState(key, state) {
  bundleBuilderStates.set(key, state);
  try {
    localStorage.setItem(BUNDLE_BUILDER_STORAGE_PREFIX + key, JSON.stringify(state));
  } catch (e) {
    // Storage full or blocked: the in-memory copy still carries the shopper through this page.
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[data-tier-tab]'));
        this.options = Array.from(this.querySelectorAll('[data-bundle-option]'));
        this.pills = this.querySelector('[data-bundle-pills]');
        this.headingEl = this.querySelector('[data-bundle-heading]');
        this.hintEl = this.querySelector('[data-bundle-hint]');
        this.pricesEl = this.querySelector('[data-bundle-prices]');
        this.compareEl = this.querySelector('[data-price-compare]');
        this.finalEl = this.querySelector('[data-price-final]');
        this.saveEl = this.querySelector('[data-price-save]');
        this.openButton = this.querySelector('[data-open-picker]');
        this.addButton = this.querySelector('[data-add-bundle]');
        this.errorEl = this.querySelector('[data-bundle-error]');
        this.modal = this.querySelector('[data-picker-modal]');
        this.modalCount = this.querySelector('[data-picker-count]');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        if (!this.tabs.length || !this.options.length) return;

        // Each tab keeps its own selection, so switching tabs never drops what was picked in another.
        this.tierSelections = new Map();
        this.selected = [];
        this.submitting = false;
        this.lastFocused = null;
        this.onKeydown = this.onKeydown.bind(this);
        this.onPageShow = this.onPageShow.bind(this);
        window.addEventListener('pageshow', this.onPageShow);

        if (document.querySelector('cart-drawer')) this.addButton.setAttribute('aria-haspopup', 'dialog');

        this.tabs.forEach((tab) =>
          tab.addEventListener('click', () => {
            this.selectTier(tab);
            this.scrollTabIntoView(tab);
          })
        );
        this.options.forEach((option) => option.addEventListener('click', () => this.addItem(option)));
        this.querySelectorAll('[data-picker-close]').forEach((el) =>
          el.addEventListener('click', () => this.closePicker())
        );
        this.openButton.addEventListener('click', () => this.openPicker());
        this.addButton.addEventListener('click', () => this.addBundleToCart());

        // .product__info-wrapper can carry a transform from the reveal-on-scroll animation, which
        // would make it the containing block for this position: fixed modal. Moving the modal to
        // the body keeps it covering the viewport. References above are cached first.
        document.body.appendChild(this.modal);

        this.state = readBundleBuilderState(this.dataset.storageKey || 'default');

        const restoredTab = this.tabs.find((tab) => parseInt(tab.dataset.quantity, 10) === this.state.activeQuantity);
        this.selectTier(restoredTab || this.tabs.find((tab) => tab.classList.contains('is-active')) || this.tabs[0]);
      }

      disconnectedCallback() {
        // The theme editor tears this element down on every settings change, so the key handler,
        // scroll lock and the body-level modal all have to be cleaned up by hand.
        document.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('pageshow', this.onPageShow);
        if (this.modal && !this.modal.hidden) document.body.classList.remove('overflow-hidden');
        if (this.modal && this.modal.parentElement === document.body) this.modal.remove();
      }

      // Back-button restores from the page cache can leave the button mid-submit.
      onPageShow() {
        if (!this.submitting) return;
        this.submitting = false;
        this.addButton.classList.remove('loading');
        this.render();
      }

      get required() {
        return this.activeTab ? parseInt(this.activeTab.dataset.quantity, 10) : 0;
      }

      get discountPercent() {
        return this.activeTab ? parseFloat(this.activeTab.dataset.discountPercent || 0) || 0 : 0;
      }

      selectTier(tab) {
        if (!tab) return;

        this.activeTab = tab;
        this.tabs.forEach((item) => {
          const isActive = item === tab;
          item.classList.toggle('is-active', isActive);
          item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        if (!this.tierSelections.has(tab)) {
          this.tierSelections.set(tab, this.buildSelection(tab));
        }
        this.selected = this.tierSelections.get(tab);

        this.clearError();
        this.render();
        this.persist(false);
      }

      scrollTabIntoView(tab) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        tab.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      // A tier starts from what the shopper last had at that size, else the tab's defaults. An
      // edited tier that was emptied stays empty, so a removed default doesn't come back on refresh.
      buildSelection(tab) {
        const quantity = parseInt(tab.dataset.quantity, 10) || 0;
        const defaults = (tab.dataset.defaults || '').split(',');
        const saved = this.state.tiers[String(quantity)];

        if (!Array.isArray(saved)) return this.resolveItems(defaults, quantity);

        const restored = this.resolveItems(saved, quantity);
        if (saved.length && !restored.length) return this.resolveItems(defaults, quantity);

        return restored;
      }

      // Resolves ids against the picker options, so an id with no option (now sold out) is dropped.
      resolveItems(ids, quantity) {
        return ids
          .filter(Boolean)
          .map((variantId) => this.options.find((option) => option.dataset.variantId === variantId))
          .filter(Boolean)
          .slice(0, quantity)
          .map((option) => this.readItem(option));
      }

      persist(touchTier) {
        this.state.activeQuantity = this.required;
        if (touchTier) {
          this.state.tiers[String(this.required)] = this.selected.map((item) => item.variantId);
        }
        writeBundleBuilderState(this.dataset.storageKey || 'default', this.state);
      }

      readItem(option) {
        const img = option.querySelector('img');
        return {
          variantId: option.dataset.variantId,
          name: option.dataset.name,
          price: parseInt(option.dataset.price, 10) || 0,
          image: img?.currentSrc || img?.src || '',
        };
      }

      // The same product can fill more than one slot.
      addItem(option) {
        if (option.disabled || this.selected.length >= this.required) return;

        this.selected.push(this.readItem(option));

        this.clearError();
        this.render();
        this.persist(true);

        if (this.selected.length >= this.required) this.closePicker();
      }

      removeItem(index) {
        this.selected.splice(index, 1);
        this.clearError();
        this.render();
        this.persist(true);
      }

      unitLabel(count) {
        return count === 1 ? this.dataset.unitSingular : this.dataset.unitPlural;
      }

      render() {
        const required = this.required;
        const remaining = required - this.selected.length;

        const heading = this.activeTab.dataset.heading || this.dataset.heading || '';
        this.headingEl.textContent = heading.replace('[count]', required);

        this.pills.innerHTML = '';
        this.selected.forEach((item, index) => {
          this.pills.appendChild(this.buildPill(item, () => this.removeItem(index)));
        });
        for (let slot = 0; slot < Math.max(remaining, 0); slot += 1) {
          this.pills.appendChild(this.buildPlaceholder());
        }

        this.hintEl.textContent = remaining > 0 ? `Add ${remaining} more ${this.unitLabel(remaining)}` : '';

        this.renderPrices();

        this.openButton.disabled = this.selected.length >= required;
        this.addButton.disabled = this.selected.length !== required || this.submitting;

        if (this.modalCount) {
          this.modalCount.textContent = `${this.selected.length} of ${required} selected`;
        }
      }

      buildPill(item, onRemove) {
        const pill = document.createElement('div');
        pill.className = 'bundle-builder__pill';

        if (item.image) {
          const img = document.createElement('img');
          img.src = item.image;
          img.alt = item.name;
          img.width = 32;
          img.height = 32;
          img.loading = 'lazy';
          pill.appendChild(img);
        }

        const name = document.createElement('span');
        name.className = 'bundle-builder__pill-name';
        name.textContent = item.name;
        pill.appendChild(name);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'bundle-builder__pill-remove';
        remove.setAttribute('aria-label', `Remove ${item.name}`);
        remove.textContent = '×';
        remove.addEventListener('click', onRemove);
        pill.appendChild(remove);

        return pill;
      }

      // An empty slot doubles as a second way into the picker.
      buildPlaceholder() {
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'bundle-builder__pill bundle-builder__pill--empty';
        pill.addEventListener('click', () => this.openPicker());

        const icon = document.createElement('span');
        icon.className = 'bundle-builder__pill-placeholder';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = '+';
        pill.appendChild(icon);

        const name = document.createElement('span');
        name.className = 'bundle-builder__pill-name';
        name.textContent = this.dataset.emptyLabel;
        pill.appendChild(name);

        return pill;
      }

      renderPrices() {
        if (!this.selected.length) {
          this.pricesEl.hidden = true;
          return;
        }

        const total = this.selected.reduce((sum, item) => sum + item.price, 0);
        // A discount only applies once the tier is complete.
        const isComplete = this.selected.length === this.required;
        // Rounded to whole rupees (100 paise) so the shown total never carries stray paise.
        const discount = isComplete ? Math.min(Math.round((total * this.discountPercent) / 100 / 100) * 100, total) : 0;

        this.pricesEl.hidden = false;
        this.finalEl.textContent = this.formatMoney(total - discount);

        this.compareEl.textContent = discount > 0 ? this.formatMoney(total) : '';
        this.compareEl.hidden = discount === 0;

        this.saveEl.textContent = discount > 0 ? `${this.dataset.saveLabel} ${this.formatMoney(discount)}` : '';
        this.saveEl.hidden = discount === 0;
      }

      formatMoney(cents) {
        const amount = cents / 100;
        try {
          return new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency: this.dataset.currency || 'INR',
            maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
          }).format(amount);
        } catch (e) {
          return amount.toFixed(2);
        }
      }

      openPicker() {
        if (this.selected.length >= this.required) return;

        this.lastFocused = document.activeElement;
        this.modal.hidden = false;
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', this.onKeydown);

        const firstAvailable = this.options.find((option) => !option.disabled);
        if (firstAvailable) firstAvailable.focus();
      }

      closePicker() {
        this.modal.hidden = true;
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keydown', this.onKeydown);

        if (this.lastFocused && !this.lastFocused.disabled && this.lastFocused.isConnected) {
          this.lastFocused.focus();
        } else {
          this.addButton.focus();
        }
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          this.closePicker();
          return;
        }
        if (event.key !== 'Tab') return;

        const focusable = Array.from(this.modal.querySelectorAll('button:not([disabled])'));
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        const outside = !this.modal.contains(active);

        if (event.shiftKey && (active === first || outside)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (active === last || outside)) {
          event.preventDefault();
          first.focus();
        }
      }

      get cartItems() {
        const counts = this.selected.reduce(
          (map, item) => map.set(item.variantId, (map.get(item.variantId) || 0) + 1),
          new Map()
        );
        return Array.from(counts, ([variantId, quantity]) => ({
          id: parseInt(variantId, 10),
          quantity,
          // Marks the lines as one bundle for a cart/checkout discount; the underscore hides it.
          properties: { _bundle: `Bundle of ${this.required}` },
        }));
      }

      addBundleToCart() {
        if (this.submitting || this.selected.length !== this.required) return;

        const items = this.cartItems;
        this.submitting = true;
        this.addButton.classList.add('loading');
        this.addButton.disabled = true;
        this.clearError();

        const body = { items };
        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          this.cart.setActiveElement(this.addButton);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(body);

        const linesUpdateDeferred = this.createCartLinesUpdateEvent(items);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              const message = response.description || response.message;
              publish(PUB_SUB_EVENTS.cartError, { source: 'bundle-builder', errors: response.errors || message, message });
              this.showError(message);
              linesUpdateDeferred?.reject(new Error(message));
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const cartDataPromise =
              typeof CartItems !== 'undefined'
                ? CartItems.fetchCartData()
                : fetch(`${routes.cart_url}.json`).then((cartResponse) => cartResponse.json());

            return cartDataPromise.then((cart) => {
              if (cart) {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-builder', cartData: cart });
                this.resolveCartLinesUpdate(linesUpdateDeferred, cart);
              }
              this.cart.renderContents(response);
            });
          })
          .catch((error) => {
            console.error(error);
            this.showError(window.cartStrings?.error);
            linesUpdateDeferred?.reject(error);
          })
          .finally(() => {
            this.submitting = false;
            this.addButton.classList.remove('loading');
            this.render();
          });
      }

      createCartLinesUpdateEvent(items) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred, cart) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!deferred || !CartLinesUpdateEvent) return;
        if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
      }

      showError(message) {
        this.errorEl.textContent = message || '';
        this.errorEl.hidden = !message;
      }

      clearError() {
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }
    }
  );
}
